// 2026-06-25: Smart Buddy HMI — Work Order Queue button + modal.
// Compact pill button in header shows queue count + active op badge.
// Click opens modal with full cards (START/PAUSE/CLAIM/RESUME).
import React, { useCallback, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { getOperatorQueue, startOperation, claimOperation, pauseOperation, resumeOperation }
  from '../../Services/app/workorderservice';

const BR = {
  orange: '#f97316', blue: '#2563eb', green: '#16a34a', red: '#dc2626',
  amber: '#f59e0b', dark: '#111827', muted: '#6b7280', border: '#e5e7eb', bg: '#f9fafb',
};

const PRIO_COLOR = { URGENT: '#dc2626', HIGH: '#f97316', NORMAL: '#2563eb', LOW: '#6b7280' };
const SHOP_COLOR = {
  MACHINE_SHOP: '#2563eb', ASSEMBLY: '#16a34a', DEBURRING: '#f59e0b',
  SPECIAL_PROCESS: '#dc2626', INSPECTION: '#9333ea', PACKING: '#6b7280', OTHER: '#9ca3af',
};

function fmtMin(min) {
  if (min == null || isNaN(min)) return '—';
  const n = Number(min);
  if (n < 60) return n.toFixed(0) + 'm';
  return Math.floor(n / 60) + 'h ' + Math.round(n % 60) + 'm';
}

export default function OperatorQueueButton({ customerId, operator, currentMachine, shiftName, onRunningChange }) {
  const [queue, setQueue] = useState({ count: 0, operations: [] });
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);
  const [claimOp, setClaimOp] = useState(null);
  const [claimQty, setClaimQty] = useState(0);
  const [claimScrap, setClaimScrap] = useState(0);
  const [focusIdx, setFocusIdx] = useState(0);
  const [pieceCount, setPieceCount] = useState({});

  const operatorId = operator?.operatorid || operator?.id || '';
  const operatorName = operator?.operatorname || operator?.name || '';
  const machineId = currentMachine?.id || null;

  const refresh = useCallback(async () => {
    if (!customerId) return;
    try {
      const q = await getOperatorQueue(customerId, machineId ? { machine_id: machineId } : {});
      setQueue(q);
      setErr(null);
    } catch (e) { setErr(e?.response?.data?.message || e.message); }
  }, [customerId, machineId]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 15000);
    return () => clearInterval(id);
  }, [refresh]);

  async function handleStart(op) {
    setBusy(op.id);
    try {
      await startOperation(op.id, {
        customer_id: customerId, operator_id: operatorId, operator_name: operatorName,
        machine_id: machineId, machine_name: currentMachine?.name,
      });
      refresh();
    } catch (e) { setErr(e?.response?.data?.message || e.message); }
    finally { setBusy(null); }
  }
  async function handleResume(op) {
    setBusy(op.id);
    try {
      await resumeOperation(op.id, {
        customer_id: customerId, operator_id: operatorId, operator_name: operatorName,
        machine_id: machineId, machine_name: currentMachine?.name,
      });
      refresh();
    } catch (e) { setErr(e?.response?.data?.message || e.message); }
    finally { setBusy(null); }
  }
  // Phase 13 2026-06-26: reason picker before pause
  async function handlePause(op) {
    const REASONS = [
      { code: 'BREAKDOWN',     label: '🛠 Machine breakdown' },
      { code: 'TOOLING_CHANGE',label: '🔧 Tooling change' },
      { code: 'MATERIAL_SHORT',label: '📦 Material shortage' },
      { code: 'QUALITY_ISSUE', label: '⚠ Quality issue' },
      { code: 'SETUP_CHANGE',  label: '⚙ Setup change' },
      { code: 'OPERATOR_BREAK',label: '☕ Operator break / lunch' },
      { code: 'POWER_OUTAGE',  label: '⚡ Power outage' },
      { code: 'MAINTENANCE',   label: '🧰 Scheduled maintenance' },
      { code: 'WAIT_INSTRUCT', label: '⏳ Waiting for instructions' },
      { code: 'OTHER',         label: '✎ Other (enter note)' },
    ];
    const optionsHtml = REASONS.map(r =>
      `<option value="${r.code}">${r.label}</option>`
    ).join('');
    const { value: form } = await Swal.fire({
      title: `Pause ${op.wo_number} · ${op.op_seq}?`,
      html: `
        <div style="text-align:left; font-size:13px;">
          <label style="font-weight:700; display:block; margin-bottom:6px;">Reason for pause</label>
          <select id="swal-pause-reason"
                  style="width:100%; padding:10px 12px; font-size:14px; font-weight:600;
                          border:1.5px solid #d1d5db; border-radius:6px; background:#fff;">
            <option value="">— Select a reason —</option>
            ${optionsHtml}
          </select>
          <label style="font-weight:700; display:block; margin-top:14px; margin-bottom:6px;">Note (optional)</label>
          <textarea id="swal-pause-note" rows="2" placeholder="Extra detail (e.g. tool number, ticket #)…"
                    style="width:100%; padding:8px 10px; font-size:13px; border:1.5px solid #d1d5db; border-radius:6px;
                            font-family:inherit; resize:vertical;"></textarea>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: '⏸ Pause',
      cancelButtonText: 'Keep working',
      confirmButtonColor: '#f97316',
      reverseButtons: true,
      focusConfirm: false,
      preConfirm: () => {
        const code = document.getElementById('swal-pause-reason').value;
        const note = document.getElementById('swal-pause-note').value.trim();
        if (!code) {
          Swal.showValidationMessage('Please select a reason');
          return false;
        }
        if (code === 'OTHER' && !note) {
          Swal.showValidationMessage('A note is required when reason is "Other"');
          return false;
        }
        const label = REASONS.find(r => r.code === code)?.label || code;
        return { code, label, note };
      },
    });
    if (!form) return;
    const reasonText = form.note ? `${form.label} — ${form.note}` : form.label;
    setBusy(op.id);
    try {
      await pauseOperation(op.id, {
        customer_id: customerId, operator_id: operatorId, operator_name: operatorName,
        pause_reason: reasonText,
        pause_reason_code: form.code,
      });
      refresh();
    } catch (e) { setErr(e?.response?.data?.message || e.message); }
    finally { setBusy(null); }
  }
  function openClaim(op) {
    setClaimOp(op);
    const live = pieceCount[op.id] || 0;
    const done = Number(op.actual_qty_good) || 0;
    const remaining = Math.max(0, (Number(op.planned_qty) || 0) - done);
    // Pre-fill: live counter if any clicks, else what is still remaining
    setClaimQty(live > 0 ? String(live) : String(remaining || op.planned_qty || 0));
    setClaimScrap(0);
  }
  async function doClaim() {
    if (!claimOp) return;
    const completedOpId = claimOp.id;
    const qtyGood = parseInt(claimQty, 10) || 0;
    const completedWoOp = `${claimOp.wo_number} ${claimOp.op_seq}`;
    setBusy(completedOpId);
    try {
      await claimOperation(completedOpId, {
        customer_id: customerId, operator_id: operatorId, operator_name: operatorName,
        qty_good: qtyGood,
        qty_scrap: parseInt(claimScrap, 10) || 0,
        claim_source: 'manual_hmi',
      });
      // Phase 13 v3: close ONLY the qty modal, keep the queue popup open
      setClaimOp(null);
      // Reset live piece counter for the completed op
      setPieceCount(prev => { const next = { ...prev }; delete next[completedOpId]; return next; });
      // Toast success — non-blocking, ~1.5s
      Swal.fire({
        toast: true, position: 'top-end', timer: 1800, showConfirmButton: false,
        icon: 'success',
        title: `✓ ${completedWoOp} completed`,
        text: `${qtyGood} pcs submitted`,
      });
      // Refresh queue; after refresh, advance focus to next op (or stay if was last)
      await refresh();
      // The completed op disappears from queue, so focusIdx implicitly moves to next.
      // If we were on the last item, clamp back so the user lands on the new last.
      setFocusIdx(prev => Math.max(0, Math.min(prev, (queue.operations?.length || 1) - 2)));
    } catch (e) { setErr(e?.response?.data?.message || e.message); }
    finally { setBusy(null); }
  }

  const ops = queue.operations || [];
  const runningOp = ops.find(o => o.status === 'IN_PROGRESS' && o.claimed_by_operator_id === operatorId);
  useEffect(() => { if (typeof onRunningChange === 'function') onRunningChange(runningOp || null); }, [runningOp?.id, runningOp?.wo_number, runningOp?.op_seq]);
  const urgentCount = ops.filter(o => o.priority === 'URGENT').length;
  const ready = customerId && operatorId;

  // ─── BUTTON (header pill) ──────────────────────────────────────────────────
  const buttonStyle = {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '7px 14px',
    background: runningOp ? BR.green : queue.count > 0 ? BR.orange : '#fff',
    color: runningOp || queue.count > 0 ? '#fff' : BR.dark,
    border: runningOp ? `2px solid ${BR.green}` : `1.5px solid ${queue.count > 0 ? BR.orange : BR.border}`,
    borderRadius: 8,
    fontSize: 12, fontWeight: 800, cursor: ready ? 'pointer' : 'not-allowed',
    boxShadow: runningOp ? '0 2px 8px rgba(22,163,74,0.35)' : '0 1px 3px rgba(0,0,0,0.06)',
    animation: runningOp ? 'queue-btn-pulse 1.6s ease-in-out infinite' : 'none',
    opacity: ready ? 1 : 0.5,
  };

  return (
    <>
      <button onClick={() => ready && setOpen(true)} disabled={!ready} title={ready ? 'Open Work Order Queue' : 'Sign in as operator first'}
              style={buttonStyle}>
        <span style={{ fontSize: 14 }}>📋</span>
        <span>WO QUEUE</span>
        {queue.count > 0 && (
          <span style={{ padding: '1px 7px', borderRadius: 10, background: '#fff',
                          color: runningOp ? BR.green : BR.orange, fontSize: 11, fontWeight: 900 }}>
            {queue.count}
          </span>
        )}
        {urgentCount > 0 && !runningOp && (
          <span style={{ padding: '1px 6px', borderRadius: 10, background: BR.red,
                          color: '#fff', fontSize: 10, fontWeight: 800 }}>
            !{urgentCount}
          </span>
        )}
        {runningOp && (
          <span style={{ fontSize: 10, fontWeight: 700, opacity: 0.85 }}>
            · {runningOp.op_seq}
          </span>
        )}
      </button>

      <style>{`
        @keyframes queue-btn-pulse {
          0%, 100% { box-shadow: 0 2px 8px rgba(22,163,74,0.35); }
          50%      { box-shadow: 0 0 0 6px rgba(22,163,74,0.20), 0 2px 12px rgba(22,163,74,0.45); }
        }
      `}</style>

      {/* ─── MODAL ────────────────────────────────────────────────────────── */}
      {open && (
        <div onClick={() => setOpen(false)}
             style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9000,
                       display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}>
          <div onClick={e => e.stopPropagation()}
               style={{ background: '#fff', borderRadius: 12, width: 'min(900px, 95vw)',
                         maxHeight: '85vh', overflow: 'auto', padding: 0 }}>
            {/* Modal header */}
            <div style={{ padding: '16px 22px', borderBottom: `1px solid ${BR.border}`,
                          display: 'flex', alignItems: 'center', gap: 12, position: 'sticky', top: 0,
                          background: '#fff', zIndex: 1 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: BR.dark }}>
                  📋 Work Order Queue
                  {queue.count > 0 && (
                    <span style={{ marginLeft: 10, padding: '2px 10px', borderRadius: 10, background: BR.orange,
                                    color: '#fff', fontSize: 12, fontWeight: 800 }}>{queue.count}</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: BR.muted, marginTop: 2 }}>
                  {operatorName} {currentMachine?.name ? `· filtered to ${currentMachine.name}` : ''} · auto-refresh 15s
                </div>
              </div>
              <a href="/operator-queue" style={{ fontSize: 12, color: BR.blue, fontWeight: 700, textDecoration: 'none' }}>
                Full Queue Page →
              </a>
              <button onClick={() => setOpen(false)}
                      style={{ background: 'none', border: 'none', fontSize: 28, lineHeight: 1, cursor: 'pointer',
                                color: BR.muted, padding: 0, width: 32 }}>×</button>
            </div>

            {/* Modal body */}
            <div style={{ padding: 18 }}>
              {err && (
                <div style={{ padding: 10, background: '#fee2e2', color: '#7a0000', borderRadius: 5,
                              fontSize: 12, marginBottom: 10, fontWeight: 700 }}>⚠ {err}</div>
              )}

              {ops.length === 0 ? (
                <div style={{ padding: 30, textAlign: 'center', color: BR.muted, fontSize: 13 }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
                  No work orders queued for {currentMachine?.name || 'this customer'}.
                </div>
              ) : (
                <div>
                  {(() => {
                    const focusOp = ops[Math.min(focusIdx, ops.length - 1)];
                    if (!focusOp) return null;
                    // Phase 13 v5: counter = backend cumulative + current-session pending
                    const submittedGood = Number(focusOp.actual_qty_good) || 0;
                    const pendingPieces = pieceCount[focusOp.id] || 0;
                    const liveCount = submittedGood + pendingPieces;
                    const bumpPiece = () => setPieceCount(prev => ({ ...prev, [focusOp.id]: (prev[focusOp.id] || 0) + 1 }));
                    const resetPiece = () => setPieceCount(prev => ({ ...prev, [focusOp.id]: 0 }));
                    return [focusOp].map(op => {
                    const isRunning = op.status === 'IN_PROGRESS';
                    const isPaused = op.status === 'ON_HOLD';
                    const isReady = op.status === 'READY';
                    const borderC = isRunning ? BR.green : isPaused ? BR.amber : BR.border;
                    return (
                      <div key={op.id} style={{ background: '#fff', border: `2px solid ${borderC}`,
                                                  borderRadius: 10, padding: 12, position: 'relative' }}>
                        {isRunning && (
                          <span style={{ position: 'absolute', top: 8, right: 10, padding: '2px 8px',
                                          borderRadius: 10, background: BR.green, color: '#fff', fontSize: 10, fontWeight: 800 }}>
                            ▶ RUNNING
                          </span>
                        )}
                        {isPaused && (
                          <span style={{ position: 'absolute', top: 8, right: 10, padding: '2px 8px',
                                          borderRadius: 10, background: BR.amber, color: '#fff', fontSize: 10, fontWeight: 800 }}>
                            ⏸ PAUSED
                          </span>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                          <span style={{ padding: '2px 7px', borderRadius: 3, fontSize: 10, fontWeight: 800, color: '#fff',
                                          background: PRIO_COLOR[op.priority] || BR.muted }}>{op.priority}</span>
                          <span style={{ padding: '2px 7px', borderRadius: 3, fontSize: 10, fontWeight: 800, color: '#fff',
                                          background: SHOP_COLOR[op.work_center_shop_type] || BR.muted }}>{op.work_center_shop_type}</span>
                        </div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: BR.dark }}>
                          <span style={{ fontFamily: 'monospace' }}>{op.op_seq}</span>
                          {' · '}{op.op_name}
                        </div>
                        <div style={{ fontSize: 11, color: BR.muted, marginTop: 2 }}>
                          <strong style={{ fontFamily: 'monospace', color: BR.dark }}>{op.wo_number}</strong>
                          {' · '}{op.part_name || op.part_number}
                          {' · qty '}{op.planned_qty}
                        </div>
                        <div style={{ fontSize: 10, color: BR.muted, marginTop: 2 }}>
                          @ {op.work_center_code}{op.machine_name ? ` · ${op.machine_name}` : ''} · {fmtMin(op.planned_time_min)} planned
                        </div>
                        {/* Phase 13 — live piece counter visible while running */}
                        {isRunning && (
                          <div style={{ marginTop: 10, padding: 12, background: '#ecfdf5', border: '2px solid ' + BR.green,
                                         borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{ fontSize: 12, fontWeight: 800, color: '#065f46' }}>
                              ✓ Pieces done<br/>
                              <span style={{ fontSize: 32, color: BR.green }}>{liveCount}</span>
                              <span style={{ fontSize: 13, color: BR.muted }}> / {op.planned_qty}</span>
                            </div>
                            <button onClick={bumpPiece}
                                    style={{ flex: 1, padding: '18px 8px', background: BR.green, color: '#fff', border: 'none',
                                              borderRadius: 8, fontSize: 18, fontWeight: 800, cursor: 'pointer',
                                              boxShadow: '0 2px 8px rgba(0,0,0,0.18)' }}>
                              +1 PIECE
                            </button>
                            {liveCount > 0 && (
                              <button onClick={resetPiece}
                                      style={{ padding: '8px 10px', background: '#fff', color: BR.muted,
                                                border: '1px solid ' + BR.border, borderRadius: 5, fontSize: 12,
                                                fontWeight: 700, cursor: 'pointer' }}>↺</button>
                            )}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                          {isReady && (
                            <button onClick={() => handleStart(op)} disabled={busy === op.id}
                                    style={{ flex: 1, padding: '12px 12px', background: BR.green, color: '#fff',
                                              border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
                              ✓ CLAIM
                            </button>
                          )}
                          {isRunning && (
                            <>
                              <button onClick={() => handlePause(op)} disabled={busy === op.id}
                                      style={{ flex: 1, padding: '12px 12px', background: BR.amber, color: '#fff',
                                                border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
                                ⏸ PAUSE
                              </button>
                              <button onClick={() => openClaim(op)} disabled={busy === op.id}
                                      style={{ flex: 1.4, padding: '12px 12px', background: BR.green, color: '#fff',
                                                border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
                                ✓ COMPLETE
                              </button>
                            </>
                          )}
                          {isPaused && (
                            <button onClick={() => handleResume(op)} disabled={busy === op.id}
                                    style={{ flex: 1, padding: '8px 12px', background: BR.blue, color: '#fff',
                                              border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>
                              ▶ RESUME
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  });
                  })()}
                  {ops.length > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
                      <button onClick={() => setFocusIdx(i => Math.max(0, i - 1))} disabled={focusIdx === 0}
                              style={{ padding: '10px 18px', background: focusIdx === 0 ? '#f3f4f6' : BR.dark,
                                        color: focusIdx === 0 ? BR.muted : '#fff',
                                        border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 800,
                                        cursor: focusIdx === 0 ? 'not-allowed' : 'pointer' }}>
                        ← Prev
                      </button>
                      <div style={{ flex: 1, textAlign: 'center', fontSize: 12, color: BR.muted, fontWeight: 700 }}>
                        WO {Math.min(focusIdx + 1, ops.length)} of {ops.length}
                      </div>
                      <button onClick={() => setFocusIdx(i => Math.min(ops.length - 1, i + 1))} disabled={focusIdx >= ops.length - 1}
                              style={{ padding: '10px 18px', background: focusIdx >= ops.length - 1 ? '#f3f4f6' : BR.dark,
                                        color: focusIdx >= ops.length - 1 ? BR.muted : '#fff',
                                        border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 800,
                                        cursor: focusIdx >= ops.length - 1 ? 'not-allowed' : 'pointer' }}>
                        Next →
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── CLAIM MODAL ─────────────────────────────────────────────────── */}
      {claimOp && (
        <div onClick={() => setClaimOp(null)}
             style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999,
                       display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()}
               style={{ background: '#fff', borderRadius: 12, width: 460, padding: 22 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: BR.dark }}>
              Complete {claimOp.op_seq} — {claimOp.op_name}
            </h3>
            <div style={{ fontSize: 12, color: BR.muted, marginTop: 4, marginBottom: 16 }}>
              {claimOp.wo_number} · {claimOp.part_name || claimOp.part_number}
            </div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, marginBottom: 4 }}>Qty Good</label>
            <input type="number" value={claimQty} onChange={e => setClaimQty(e.target.value)} autoFocus
                   style={{ width: '100%', padding: '12px 14px', fontSize: 24, fontWeight: 800, color: BR.green,
                             border: `2px solid ${BR.green}`, borderRadius: 6, textAlign: 'center' }} />
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, marginTop: 12, marginBottom: 4 }}>Qty Scrap (rejects)</label>
            <input type="number" value={claimScrap} onChange={e => setClaimScrap(e.target.value)}
                   style={{ width: '100%', padding: '10px 12px', fontSize: 18, fontWeight: 700,
                             color: claimScrap > 0 ? BR.red : BR.dark,
                             border: `1px solid ${claimScrap > 0 ? BR.red : BR.border}`, borderRadius: 5, textAlign: 'center' }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
              <button onClick={() => setClaimOp(null)}
                      style={{ flex: 1, padding: 12, background: '#fff', color: BR.dark,
                                border: `1px solid ${BR.border}`, borderRadius: 6, fontWeight: 700, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={doClaim} disabled={busy}
                      style={{ flex: 2, padding: 12, background: busy ? BR.muted : BR.green, color: '#fff',
                                border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
                {busy ? 'Submitting…' : `✓ COMPLETE ${claimOp.op_seq}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
